function normalizePoint(value) {
  if (!value) return null;
  if (value.point || value.location || value.coordinate || value.coordinates || value.center) {
    return normalizePoint(value.point || value.location || value.coordinate || value.coordinates || value.center);
  }
  if (Array.isArray(value) && value.length >= 2) {
    const a = Number(value[0]);
    const b = Number(value[1]);
    if (!Number.isFinite(a) || !Number.isFinite(b)) return null;

    // Heuristic: [lng, lat] is common GeoJSON ordering.
    if (Math.abs(a) <= 180 && Math.abs(b) <= 90) {
      return { lat: b, lng: a };
    }
    // Fallback [lat, lng]
    if (Math.abs(a) <= 90 && Math.abs(b) <= 180) {
      return { lat: a, lng: b };
    }
    return null;
  }

  const lat = Number(value.lat ?? value.latitude ?? value.y);
  const lng = Number(value.lng ?? value.lon ?? value.longitude ?? value.long ?? value.x);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return { lat, lng };
}

function normalizePolygon(raw) {
  if (!Array.isArray(raw)) return null;
  const points = raw
    .map((item) => normalizePoint(item?.point ?? item?.location ?? item?.coordinate ?? item?.coordinates ?? item))
    .filter(Boolean);
  if (points.length < 3) return null;
  return points;
}

function toRadians(degrees) {
  return (degrees * Math.PI) / 180;
}

function haversineMeters(a, b) {
  const R = 6371000;
  const dLat = toRadians(b.lat - a.lat);
  const dLng = toRadians(b.lng - a.lng);
  const lat1 = toRadians(a.lat);
  const lat2 = toRadians(b.lat);

  const s =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.sin(dLng / 2) * Math.sin(dLng / 2) * Math.cos(lat1) * Math.cos(lat2);

  return 2 * R * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s));
}

function pointInPolygon(point, polygon) {
  // Ray-casting algorithm for point-in-polygon.
  let inside = false;
  const x = point.lng;
  const y = point.lat;

  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i].lng;
    const yi = polygon[i].lat;
    const xj = polygon[j].lng;
    const yj = polygon[j].lat;

    const intersects =
      ((yi > y) !== (yj > y)) &&
      x < ((xj - xi) * (y - yi)) / ((yj - yi) || 1e-12) + xi;

    if (intersects) inside = !inside;
  }

  return inside;
}

export function normalizeTournamentGeofence(tournamentDocData) {
  if (!tournamentDocData || typeof tournamentDocData !== "object") return null;

  const geofence = Array.isArray(tournamentDocData.geofence)
    ? { boundaries: tournamentDocData.geofence }
    : (tournamentDocData.geofence || {});
  const enabledRaw =
    geofence.enabled ??
    geofence.active ??
    geofence.isEnabled ??
    tournamentDocData.geofenceEnabled ??
    tournamentDocData.boundaryEnabled;
  const enabled = enabledRaw == null ? null : Boolean(enabledRaw);

  const rawPolygon =
    geofence.polygon ||
    geofence.polygons ||
    geofence.coordinates ||
    geofence.boundary ||
    geofence.boundaries ||
    geofence.points ||
    geofence.vertices ||
    tournamentDocData.boundary ||
    tournamentDocData.boundaries ||
    tournamentDocData.boundaryPoints ||
    tournamentDocData.points ||
    tournamentDocData.vertices ||
    tournamentDocData.geofencePolygon ||
    tournamentDocData.boundaryPolygon;

  const polygon = normalizePolygon(rawPolygon);
  if (polygon) {
    if (enabled === false) return null;
    return {
      enabled: true,
      type: "polygon",
      polygon,
    };
  }

  const center = normalizePoint(
    geofence.center || geofence.origin || tournamentDocData.geofenceCenter || tournamentDocData.boundaryCenter
  );

  const radiusMeters = Number(
    geofence.radiusMeters ||
      geofence.radiusM ||
      tournamentDocData.geofenceRadiusMeters ||
      tournamentDocData.boundaryRadiusMeters ||
      0
  );

  if (center && Number.isFinite(radiusMeters) && radiusMeters > 0) {
    if (enabled === false) return null;
    return {
      enabled: true,
      type: "circle",
      center,
      radiusMeters,
    };
  }

  return null;
}

export function hasTournamentAreaRestriction(geofence) {
  return Boolean(geofence && geofence.enabled && (geofence.type === "polygon" || geofence.type === "circle"));
}

// Best-effort geographic center for a tournament, derived from whatever
// boundary geometry the dashboard app wrote. Used to look up a human region
// (e.g. US state) for search/filtering — independent of whether the geofence
// is enabled for in-app boundary enforcement.
export function tournamentGeoCenter(tournamentDocData) {
  if (!tournamentDocData || typeof tournamentDocData !== "object") return null;

  const geofence = Array.isArray(tournamentDocData.geofence)
    ? { boundaries: tournamentDocData.geofence }
    : (tournamentDocData.geofence || {});

  const rawPolygon =
    geofence.polygon ||
    geofence.polygons ||
    geofence.coordinates ||
    geofence.boundary ||
    geofence.boundaries ||
    geofence.points ||
    geofence.vertices ||
    tournamentDocData.boundary ||
    tournamentDocData.boundaries ||
    tournamentDocData.boundaryPoints ||
    tournamentDocData.points ||
    tournamentDocData.vertices ||
    tournamentDocData.geofencePolygon ||
    tournamentDocData.boundaryPolygon;

  const polygon = normalizePolygon(rawPolygon);
  if (polygon && polygon.length) {
    const n = polygon.length;
    const sum = polygon.reduce(
      (acc, pt) => ({ lat: acc.lat + pt.lat, lng: acc.lng + pt.lng }),
      { lat: 0, lng: 0 }
    );
    return { lat: sum.lat / n, lng: sum.lng / n };
  }

  const center = normalizePoint(
    geofence.center ||
      geofence.origin ||
      tournamentDocData.geofenceCenter ||
      tournamentDocData.boundaryCenter ||
      tournamentDocData.location ||
      tournamentDocData.center ||
      tournamentDocData.coordinates
  );
  return center || null;
}

export function isLocationInsideGeofence(location, geofence) {
  const p = normalizePoint(location);
  if (!p || !hasTournamentAreaRestriction(geofence)) return true;

  if (geofence.type === "polygon") {
    return pointInPolygon(p, geofence.polygon || []);
  }

  if (geofence.type === "circle") {
    const center = normalizePoint(geofence.center);
    if (!center) return true;
    return haversineMeters(p, center) <= Number(geofence.radiusMeters || 0);
  }

  return true;
}
